import React, { useEffect, useRef } from "react";
import { useGoogleMaps } from "../../contexts/GoogleMapsContext";

export interface PlaceSelection {
  address: string;
  lat: number;
  lng: number;
}

interface PlaceAutocompleteInputProps {
  value: string;
  onChange: (value: string) => void;
  onPlaceSelected: (place: PlaceSelection) => void;
  onFocus?: () => void;
  placeholder?: string;
  className?: string;
}

// Wraps the classic google.maps.places.Autocomplete widget around a plain
// text input - it manages its own suggestion dropdown (appended to <body>),
// so callers should not render a custom suggestions list alongside this.
const PlaceAutocompleteInput: React.FC<PlaceAutocompleteInputProps> = ({
  value,
  onChange,
  onPlaceSelected,
  onFocus,
  placeholder,
  className,
}) => {
  const { isLoaded } = useGoogleMaps();
  const inputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const onPlaceSelectedRef = useRef(onPlaceSelected);
  onPlaceSelectedRef.current = onPlaceSelected;

  useEffect(() => {
    if (!isLoaded || !inputRef.current || autocompleteRef.current) return;

    const autocomplete = new window.google.maps.places.Autocomplete(
      inputRef.current,
      {
        componentRestrictions: { country: "in" },
        fields: ["formatted_address", "geometry", "name"],
      }
    );

    autocomplete.addListener("place_changed", () => {
      const place = autocomplete.getPlace();
      const location = place.geometry?.location;
      if (!location) return;

      // For named POIs (airports, landmarks) formatted_address is often just
      // the surrounding locality (e.g. "Ernakulam, Kerala 683111, India" for
      // Cochin International Airport) - prepend the actual place name so the
      // selection the user made isn't silently swapped out for its locality.
      const { name, formatted_address } = place;
      const address =
        name && formatted_address && !formatted_address.startsWith(name)
          ? `${name}, ${formatted_address}`
          : formatted_address || name || "";
      onPlaceSelectedRef.current({
        address,
        lat: location.lat(),
        lng: location.lng(),
      });
    });

    autocompleteRef.current = autocomplete;
  }, [isLoaded]);

  return (
    <input
      ref={inputRef}
      type="text"
      className={className}
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onFocus={onFocus}
    />
  );
};

export default PlaceAutocompleteInput;
